import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef
} from "@angular/core";
import { NgForm } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { State } from "@clr/angular";

import { SystemInfo, SystemInfoService, HelmChartItem } from "../service/index";
import { ErrorHandler } from "../error-handler/error-handler";
import { toPromise, DEFAULT_PAGE_SIZE } from "../utils";
import { HelmChartService } from "../service/helm-chart.service";
import { DefaultHelmIcon} from "../shared/shared.const";
import { Roles } from './../shared/shared.const';

@Component({
  selector: "hbr-helm-chart",
  template: `<div>
    <div class="row chart-tool">
        <div class="toolbar">
            <div class="row flex-items-xs-right option-right rightPos">
                <div class="flex-xs-middle">
                    <hbr-filter [withDivider]="true" filterPlaceholder="{{'HELM_CHART.FILTER_FOR_CHARTS' | translate}}"
                    [currentValue]="lastFilteredChartName"
                    (filterEvt)="updateFilterValue($event)"></hbr-filter>
                    <span class="card-btn" (click)="showCard(true)" (mouseenter)="mouseEnter('card') " (mouseleave)="mouseLeave('card')">
                        <clr-icon [ngClass]="{'is-highlight': isCardView || isHovering('card') }" shape="view-cards"></clr-icon>
                    </span>
                    <span class="list-btn" (click)="showCard(false)" (mouseenter)="mouseEnter('list') " (mouseleave)="mouseLeave('list')">
                        <clr-icon [ngClass]="{'is-highlight': !isCardView || isHovering('list') }" shape="view-list"></clr-icon>
                    </span>
                    <span class="filter-divider"></span>
                    <span class="refresh-btn" (click)="refresh()">
                        <clr-icon shape="refresh"></clr-icon>
                    </span>
                </div>
            </div>
        </div>
    </div>
    <div class="row">
        <div *ngIf="!isCardView" class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <clr-datagrid (clrDgRefresh)="refresh()" [clrDgLoading]="loading" [(clrDgSelected)]="selectedRows">
                <clr-dg-action-bar>
                    <button type="button" class="btn btn-sm btn-secondary" [disabled]="!developerRoleOrAbove" (click)="onChartUpload()">
                            <clr-icon shape="upload" size="16"></clr-icon>&nbsp;{{'HELM_CHART.UPLOAD' | translate}}
                    </button>
                </clr-dg-action-bar>
                <clr-dg-column >{{'HELM_CHART.NAME' | translate}}</clr-dg-column>
                <clr-dg-column >{{'HELM_CHART.STATUS' | translate}}</clr-dg-column>
                <clr-dg-column>{{'HELM_CHART.CHARTVERSIONS' | translate}}</clr-dg-column>
                <clr-dg-column>{{'HELM_CHART.CREATED' | translate}}</clr-dg-column>
                <clr-dg-placeholder>{{'HELM_CHART.PLACEHOLDER' | translate }}</clr-dg-placeholder>
                <clr-dg-row *ngFor="let chart of charts" [clrDgItem]="chart">
                    <clr-dg-cell>
                        <span class="list-img">
                           <img  class="size-24 margin-right-12" [src]="chart.icon ?chart.icon:chartDefaultIcon"  (error)="getDefaultIcon(chart);" />
                        </span>
                        <a href="javascript:void(0)" (click)="onChartClick(chart)">{{ chart.name }}</a>
                    </clr-dg-cell>
                    <clr-dg-cell>{{ getStatusString(chart) | translate }}</clr-dg-cell>
                    <clr-dg-cell>{{ chart.total_versions }}</clr-dg-cell>
                    <clr-dg-cell>{{ chart.created | date }}</clr-dg-cell>
                </clr-dg-row>
                <clr-dg-footer>
                    <clr-dg-pagination #pagination [clrDgPageSize]="pageSize" [clrDgTotalItems]="totalCount">
                        <span *ngIf="pagination.totalItems">
                            {{pagination.firstItem + 1}} - {{pagination.lastItem + 1}} {{'HELM_CHART.OF' | translate}}
                        </span>
                        {{pagination.totalItems}} {{'HELM_CHART.ITEMS'| translate}}
                    </clr-dg-pagination>
                </clr-dg-footer>
            </clr-datagrid>
        </div>
    </div>
    <div *ngIf="isCardView" class="row card-container">
        <div *ngFor="let item of charts;" class="chart-card">
            <a let i=index; class="card clickable" (click)="onChartClick(item)">
                <div class="card-header">
                    <div class="card-icon">
                        <img  class="size-60" [src]="item.icon ?item.icon:chartDefaultIcon"  (error)="getDefaultIcon(item);" />
                    </div>
                    <div class="card-title">{{item.name}}</div>
                </div>
                <div class="card-footer">
                    <div class="row flex-items-xs-between">
                        <div>
                            <span class="version-text">{{item.total_versions}}</span>
                            <label class="card-label" *ngIf="item.total_versions !== 1">{{'HELM_CHART.CHARTVERSIONS' | translate}}</label>
                            <label class="card-label" *ngIf="item.total_versions === 1">{{'HELM_CHART.VERSION' | translate}}</label>
                        </div>
                        <div>
                            <span class="label"
                            [class.label-danger]="item.deprecated"
                            [class.label-success]="!item.deprecated"
                            >{{getStatusString(item) | translate}}</span>
                        </div>
                    </div>
                </div>
            </a>
        </div>
        <div *ngIf="loading">
            <span class="vertical-helper"></span>
            <span class="spinner"></span>
        </div>
    </div>
    <clr-modal [(clrModalOpen)]="isUploadModalOpen" [clrModalStaticBackdrop]="true" [clrModalClosable]="false">
        <h3 class="modal-title">{{'HELM_CHART.UPLOAD_TITLE' | translate | titlecase}}</h3>
        <div class="modal-body">
            <form #chartUploadForm="ngForm" enctype="multipart/form-data" (ngSubmit)="upload()">
                <section class="form-block">
                    <div class="form-group">
                        <label class="filename-label"> {{'HELM_CHART.CHART_FILE' | translate}} </label>
                        <input class="filename-input" type="text" placeholder="{{this.chartFile?.name || 'BUTTON.NO_FILE' | translate}}" disabled>
                        <label for="chart" class="btn btn-secondary btn-sm file-browser-btn">{{'BUTTON.BROWSE' | translate}}</label>
                        <input class="file-input" type="file" id="chart" name="chart" ngModel (change)="onChartFileChangeEvent($event)">
                    </div>
                    <div class="form-group">
                        <label class="filename-label"> {{'HELM_CHART.CHART_PROV' | translate}} </label>
                        <input class="filename-input" type="text" placeholder="{{this.provFile?.name || 'BUTTON.NO_FILE' | translate}}" disabled>
                        <label for="prov" class="btn btn-secondary btn-sm file-browser-btn">{{'BUTTON.BROWSE' | translate}}</label>
                        <input class="file-input" type="file" id="prov" name="prov" ngModel (change)="onProvFileChangeEvent($event)">
                    </div>
                </section>
                <div class="row flex-items-xs-right">
                    <button class="btn btn-secondary" [disabled]="isUploading" (click)="cancelUpload()">
                        <span>{{'BUTTON.CANCEL' | translate}}</span>
                    </button>
                    <button type="submit" class="btn btn-primary" [disabled]="isUploading">
                        <span>{{'HELM_CHART.UPLOAD' | translate}}</span>
                        <span *ngIf="isUploading" class="spinner spinner-inline">
                            Loading...
                        </span>
                    </button>
                </div>
            </form>
        </div>
    </clr-modal>
</div>`,
  styles: [`.chart-tool{position:relative}.chart-tool .toolbar{overflow:hidden}.chart-tool .toolbar .rightPos{position:absolute;z-index:100;right:35px;margin-top:4px}.chart-tool .toolbar .rightPos .filter-divider{display:inline-block;height:16px;width:2px;background-color:#ccc;padding-top:12px;padding-bottom:12px;position:relative;top:9px;margin-right:6px;margin-left:6px}.card-container{margin-top:21px}.card-container .chart-card{width:200px;margin:10px}.card-container .card-header .card-icon{display:flex;justify-content:center;align-items:center}.card-container .card-header .card-title{overflow:hidden;text-overflow:ellipsis;word-wrap:break-word;white-space:nowrap;text-align:center;margin:15px}.card-container .card-footer{background-color:#d7d7d7}.card-container .card-footer .version-text{font-size:1.1rem}.card-container .card-footer .card-label{width:60px;display:inline-block}.vertical-helper{display:inline-block;height:100%;vertical-align:middle}.size-24{width:24px;height:24px}.size-60{height:60px;max-width:100%}.margin-right-12{margin-right:12px}.file-input{display:none}.filename-span{overflow:hidden;text-overflow:ellipsis;word-wrap:break-word;white-space:nowrap;display:inline-block;width:50%;vertical-align:top}clr-modal .form-group{padding-left:6rem}clr-modal .form-group .filename-label{padding-top:9px}clr-modal .form-group .filename-input{margin-top:12px;width:68%}clr-modal .form-group .file-browser-btn{margin-left:15px;max-width:25%}`],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelmChartComponent implements OnInit {
  signedCon: { [key: string]: any | string[] } = {};
  @Input() projectId: number;
  @Input() projectName = "unknown";
  @Input() urlPrefix: string;
  @Input() hasSignedIn: boolean;
  @Input() projectRoleID = Roles.OTHER;
  @Input() hasProjectAdminRole: boolean;
  @Output() chartClickEvt = new EventEmitter<any>();
  @Output() chartDownloadEve = new EventEmitter<string>();
  @Input() chartDefaultIcon: string = DefaultHelmIcon;

  lastFilteredChartName: string;
  charts: HelmChartItem[] = [];
  chartsCopy: HelmChartItem[] = [];
  systemInfo: SystemInfo;
  selectedRows: HelmChartItem[] = [];
  loading = true;

  // For Upload
  isUploading = false;
  isUploadModalOpen = false;
  provFile: File;
  chartFile: File;

  // For View swtich
  isCardView: boolean;
  cardHover = false;
  listHover = false;

  pageSize: number = DEFAULT_PAGE_SIZE;
  currentPage = 1;
  totalCount = 0;
  currentState: State;

  @ViewChild('chartUploadForm') uploadForm: NgForm;

  constructor(
    private errorHandler: ErrorHandler,
    private translateService: TranslateService,
    private systemInfoService: SystemInfoService,
    private helmChartService: HelmChartService,
    private cdr: ChangeDetectorRef,
  ) {}

  public get registryUrl(): string {
    return this.systemInfo ? this.systemInfo.registry_url : "";
  }

  public get developerRoleOrAbove(): boolean {
    return this.projectRoleID === Roles.DEVELOPER || this.hasProjectAdminRole;
  }

  ngOnInit(): void {
    // Get system info for tag views
    toPromise<SystemInfo>(this.systemInfoService.getSystemInfo())
      .then(systemInfo => (this.systemInfo = systemInfo))
      .catch(error => this.errorHandler.error(error));
    this.lastFilteredChartName = "";
    this.refresh();
  }

  updateFilterValue(value: string) {
    this.lastFilteredChartName = value;
    this.refresh();
  }

  refresh() {
    this.loading = true;
    this.helmChartService
    .getHelmCharts(this.projectName)
    .finally(() => {
        let hnd = setInterval(() => this.cdr.markForCheck(), 100);
        setTimeout(() => clearInterval(hnd), 3000);
        this.loading = false;
    })
    .subscribe(
      charts => {
        this.charts = charts.filter(x => x.name.includes(this.lastFilteredChartName));
        this.chartsCopy = charts.map(x => Object.assign({}, x));
        this.totalCount = charts.length;
      },
      err => {
        this.errorHandler.error(err);
      }
    );
  }

  onChartClick(item: HelmChartItem) {
    this.chartClickEvt.emit(item.name);
  }

  resetUploadForm() {
    this.chartFile = null;
    this.provFile = null;
    this.uploadForm.reset();
  }

  onChartUpload() {
    this.resetUploadForm();
    this.isUploadModalOpen = true;
  }

  cancelUpload() {
    this.resetUploadForm();
    this.isUploadModalOpen = false;
  }

  upload() {
    if (!this.chartFile && !this.provFile) {
      return;
    }
    if (this.isUploading) { return; };
    this.isUploading = true;
    this.helmChartService
      .uploadChart(this.projectName, this.chartFile, this.provFile)
      .finally(() => {
        this.isUploading = false;
        this.isUploadModalOpen = false;
        this.refresh();
      })
      .subscribe(() => {
          this.translateService
            .get("HELM_CHART.FILE_UPLOADED")
            .subscribe(res => this.errorHandler.info(res));
        },
        err => this.errorHandler.error(err)
      );
  }

  onChartFileChangeEvent(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.chartFile = event.target.files[0];
    }
  }
  onProvFileChangeEvent(event) {
    if (event.target.files && event.target.files.length > 0) {
      this.provFile = event.target.files[0];
    }
  }


  showCard(cardView: boolean) {
    if (this.isCardView === cardView) {
      return;
    }
    this.isCardView = cardView;
  }

  mouseEnter(itemName: string) {
    if (itemName === "card") {
      this.cardHover = true;
    } else {
      this.listHover = true;
    }
  }

  mouseLeave(itemName: string) {
    if (itemName === "card") {
      this.cardHover = false;
    } else {
      this.listHover = false;
    }
  }

  isHovering(itemName: string) {
    if (itemName === "card") {
      return this.cardHover;
    } else {
      return this.listHover;
    }
  }

  getDefaultIcon(chart: HelmChartItem) {
    chart.icon = this.chartDefaultIcon;
  }

  getStatusString(chart: HelmChartItem) {
    if (chart.deprecated) {
      return "HELM_CHART.DEPRECATED";
    } else {
      return "HELM_CHART.ACTIVE";
    }
  }
}
